import { getCurrentLanguage, interpolate } from '../../translations.js';
import {
  getEffectiveVoiceMultiplier,
  getProviderCreditMultiplier,
} from '../../utils/creditMultiplier.js';
import { getVoiceById } from '../../services/voiceService.js';
import { storage } from '../../utils/storage.js';
import { storageManager } from '../../../core/storage/index.js';
import { HangoutState } from '../state/HangoutState.js';
import { getCurrentVoiceId } from './voice-settings.js';

const EXPENSIVE_VOICE_TOAST_FLAG = 'expensiveVoiceToastShown';
const EXPENSIVE_VOICE_FALLBACK =
  'Current voice for {characterName} is good but expensive. You can switch voice at any time in the settings if you wish.';

/**
 * @param {string | number | null | undefined} characterId
 * @param {string | null | undefined} characterName
 * @returns {string}
 */
const resolveCharacterName = (characterId, characterName) => {
  if (typeof characterName === 'string' && characterName.trim()) return characterName.trim();
  const session = HangoutState.getInstance()?.getCurrentSession?.();
  const match = session?.allCharacters?.find(
    (character) => String(character?.id ?? character?.characterId) === String(characterId)
  );
  if (match?.name) return String(match.name);
  return 'this character';
};

/**
 * @param {string | number | null | undefined} characterId
 * @param {{ voiceId?: string | null } | null | undefined} voiceSettings
 * @returns {Promise<number | null>}
 */
const resolveActiveVoiceMultiplier = async (characterId, voiceSettings = null) => {
  const voiceId = voiceSettings?.voiceId || getCurrentVoiceId(characterId);
  if (!voiceId) return null;

  const voice =
    (await getVoiceById(voiceId, 'voices'))
    || (await getVoiceById(voiceId, 'male_voices'));

  if (voice) return getEffectiveVoiceMultiplier(voice);

  const provider = storage.get('characterVoiceProviders')?.[String(characterId)];
  if (provider) return getProviderCreditMultiplier(provider);
  return null;
};

/**
 * Show a once-per-hangout warning when starting TTS with multiplier >= 1.
 * Persisted on currentHangoutConfig so it resets when the user starts a new chat.
 * @param {{ characterId: string | number, characterName?: string | null, voiceSettings?: object | null }} params
 */
export const maybeShowExpensiveVoiceToast = async ({
  characterId,
  characterName = null,
  voiceSettings = null,
} = {}) => {
  try {
    const hangoutConfig = await storageManager.getItem('currentHangoutConfig') || {};
    if (hangoutConfig[EXPENSIVE_VOICE_TOAST_FLAG]) return;

    const multiplier = await resolveActiveVoiceMultiplier(characterId, voiceSettings);
    if (multiplier == null || multiplier < 1) return;

    const lang = getCurrentLanguage();
    const template = window.translations?.[lang]?.hangout?.notifications?.expensive_voice_warning
      ?? window.translations?.en?.hangout?.notifications?.expensive_voice_warning
      ?? EXPENSIVE_VOICE_FALLBACK;
    const message = interpolate(template, {
      characterName: resolveCharacterName(characterId, characterName),
    });

    const { showWarning } = await import('../../utils/UnifiedNotificationSystem.js');
    showWarning(message, {
      duration: 8000,
      position: 'top-right',
      replaceExisting: true,
    });

    hangoutConfig[EXPENSIVE_VOICE_TOAST_FLAG] = true;
    await storageManager.setItem('currentHangoutConfig', hangoutConfig);
  } catch (error) {
    console.warn('maybeShowExpensiveVoiceToast failed:', error);
  }
};
